<template>
  <div class="company-access-manager">
    <div class="mb-6">
      <h4 class="mb-2 text-lg font-semibold">
        Manage Company Access for {{ user.first_name }} {{ user.last_name }}
      </h4>
      <p class="text-sm text-gray-600">
        Grant this user access to all companies with a default role, and optionally add specific
        company overrides.
      </p>
    </div>

    <div class="mb-6">
      <v-checkbox
        v-model="hasAllCompaniesAccess"
        :disabled="isLoading"
        label="Grant access to all companies"
        hideDetails
        class="mb-4"
      />

      <v-select
        v-if="hasAllCompaniesAccess"
        v-model="allCompaniesRole"
        :items="roleOptions"
        itemTitle="text"
        itemValue="value"
        label="Default Role for All Companies"
        variant="outlined"
        density="compact"
        :disabled="isLoading"
        hint="This role will apply to all companies. You can still override with specific company assignments below."
        persistentHint
      />
    </div>

    <div class="mb-6">
      <h5 class="text-md mb-3 font-semibold">
        {{ hasAllCompaniesAccess ? "Specific Company Overrides" : "Specific Company Access" }}
      </h5>
      <v-table v-if="companyAccesses.length > 0" class="company-role-table">
        <thead>
          <tr>
            <th class="text-left">Company</th>
            <th class="text-left" style="width: 200px">Role</th>
            <th class="text-left" style="width: 80px">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="access in companyAccesses" :key="access.companySlug">
            <td>{{ access.companyName }}</td>
            <td>
              <v-select
                v-model="access.role"
                :items="roleOptions"
                itemTitle="text"
                itemValue="value"
                density="compact"
                variant="outlined"
                hideDetails
                :disabled="isLoading"
              />
            </td>
            <td>
              <v-btn
                icon="mdi-delete"
                variant="text"
                size="small"
                color="error"
                :disabled="isLoading"
                @click="removeCompany(access.companySlug)"
              />
            </td>
          </tr>
        </tbody>
      </v-table>

      <div v-else class="mb-4 text-sm text-gray-500">
        {{
          hasAllCompaniesAccess ? "No company-specific overrides." : "No companies assigned."
        }}
        Click "Add Company" to get started.
      </div>

      <v-btn
        color="success"
        variant="text"
        prependIcon="mdi-plus"
        :disabled="isLoading || availableCompanies.length === 0"
        @click="openAddCompanyDialog"
      >
        Add Company
      </v-btn>
    </div>

    <div class="flex gap-4">
      <v-btn
        color="success"
        variant="flat"
        class="px-10!"
        :disabled="isLoading || (hasAllCompaniesAccess && !allCompaniesRole)"
        :loading="isLoading"
        @click="saveChanges"
      >
        <v-icon start>md:save</v-icon>
        Save Company Access
      </v-btn>

      <v-btn
        class="cancel-btn px-7!"
        variant="outlined"
        :disabled="isLoading"
        @click="$emit('cancel')"
      >
        Cancel
      </v-btn>
    </div>

    <!-- Add Company Dialog -->
    <v-dialog v-model="showAddCompanyDialog" persistent maxWidth="600" width="550">
      <v-card>
        <v-card-title class="headline">Add Company Access</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedNewCompanySlug"
            :items="availableCompanies"
            itemTitle="name"
            itemValue="slug"
            label="Select Company"
            variant="outlined"
            density="compact"
            clearable
            :autoSelectFirst="false"
            persistentPlaceholder
            class="mb-4"
          >
            <template #no-data>
              <div class="text-grey-darken-1 px-4 py-2 text-sm">No companies available</div>
            </template>
          </v-autocomplete>
          <v-select
            v-model="selectedNewCompanyRole"
            :items="roleOptions"
            itemTitle="text"
            itemValue="value"
            label="Select Role"
            variant="outlined"
            density="compact"
          />
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn class="cancel-btn px-7!" variant="outlined" @click="closeAddCompanyDialog">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            variant="flat"
            class="px-10!"
            :disabled="!selectedNewCompanySlug || !selectedNewCompanyRole"
            @click="addCompany"
          >
            <v-icon start>md:add</v-icon>
            Add Company
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import ApiCompanyAccesses from "@/apis/ApiCompanyAccesses";
import ApiUsers from "@/apis/ApiUsers";
import { useToastsStore } from "@/pinia/toastsStore";
import StringUtils from "@/utils/StringUtils";
import * as Sentry from "@sentry/vue";

const props = defineProps({
  user: { type: Object, required: true },
});

const emit = defineEmits(["saved", "cancel"]);

const toastsStore = useToastsStore();

const hasAllCompaniesAccess = ref(false);
const allCompaniesRole = ref(null);
const companyAccesses = ref([]);
const allAvailableCompanies = ref([]);
const isLoading = ref(false);
const showAddCompanyDialog = ref(false);
const selectedNewCompanySlug = ref(null);
const selectedNewCompanyRole = ref(null);
const roleOptions = ref([]);

const availableCompanies = computed(() => {
  const assignedSlugs = companyAccesses.value.map(a => a.companySlug);
  return allAvailableCompanies.value.filter(c => !assignedSlugs.includes(c.slug));
});

onMounted(async () => {
  await Promise.all([loadUserAccess(), loadRoles()]);
});

const loadRoles = async () => {
  try {
    const roles = await ApiUsers.getUserRoles();
    // Convert {member: 0, admin: 1, super_admin: 2} to dropdown format
    roleOptions.value = Object.entries(roles)
      .map(([key, value]) => ({
        value: key,
        text: StringUtils.titleize(key),
      }))
      .sort((a, b) => roles[a.value] - roles[b.value]); // Sort by role level
  } catch (error) {
    Sentry.captureException(error);
    toastsStore.create({ body: "Failed to load roles", type: "error", timeout: 5000 });
  }
};

const loadUserAccess = async () => {
  try {
    const response = await ApiCompanyAccesses.getAccessibleCompanies(props.user.id);

    // Set all-companies access checkbox and role
    hasAllCompaniesAccess.value = response.can_access_all_companies || false;
    allCompaniesRole.value = response.all_companies_access_role || null;

    // Load specific company access overrides (not all accessible companies)
    if (response.company_access_overrides) {
      companyAccesses.value = response.company_access_overrides.map(company => ({
        companyName: company.name,
        companySlug: company.slug,
        role: company.acting_role,
      }));
    }

    // Load available companies (excludes user's own company from backend)
    if (response.available_companies) {
      allAvailableCompanies.value = response.available_companies.map(company => ({
        name: company.name,
        slug: company.slug,
      }));
    }
  } catch (error) {
    console.error("Failed to load company access:", error);
    toastsStore.create({ body: "Failed to load company access", type: "error", timeout: 5000 });
  }
};

const addCompany = () => {
  const company = allAvailableCompanies.value.find(c => c.slug === selectedNewCompanySlug.value);
  if (company) {
    // Check if company already exists in the list
    const existingAccess = companyAccesses.value.find(a => a.companySlug === company.slug);
    if (existingAccess) {
      // Update the role of existing company access
      existingAccess.role = selectedNewCompanyRole.value;
      toastsStore.create({
        body: `Updated ${company.name} role to ${StringUtils.titleize(selectedNewCompanyRole.value)}`,
        type: "info",
      });
    } else {
      // Add new company access
      companyAccesses.value.push({
        companyName: company.name,
        companySlug: company.slug,
        role: selectedNewCompanyRole.value,
      });
    }
  }
  closeAddCompanyDialog();
};

const removeCompany = companySlug => {
  companyAccesses.value = companyAccesses.value.filter(a => a.companySlug !== companySlug);
};

const openAddCompanyDialog = () => {
  // Reset form state before opening
  selectedNewCompanySlug.value = null;
  selectedNewCompanyRole.value = null;
  showAddCompanyDialog.value = true;
};

const closeAddCompanyDialog = () => {
  showAddCompanyDialog.value = false;
  selectedNewCompanySlug.value = null;
  selectedNewCompanyRole.value = null;
};

const saveChanges = async () => {
  isLoading.value = true;
  try {
    // Validate: if all-companies access is checked, role must be selected
    if (hasAllCompaniesAccess.value && !allCompaniesRole.value) {
      toastsStore.create({
        body: "Please select a default role for all companies",
        type: "error",
        timeout: 5000,
      });
      isLoading.value = false;
      return;
    }

    // Always send company accesses (they serve as overrides when all-companies access is enabled)
    const companyAccessesData = companyAccesses.value.map(a => ({
      slug: a.companySlug,
      role: a.role,
    }));

    // Send both: all-companies flag + role AND specific company accesses
    await ApiCompanyAccesses.updateCompanyAccesses(
      props.user.id,
      companyAccessesData,
      hasAllCompaniesAccess.value,
      allCompaniesRole.value,
    );

    toastsStore.create({ body: "Company access updated successfully", type: "success" });
    emit("saved");
  } catch (error) {
    console.error("Failed to update company access:", error);
    toastsStore.create({ body: "Failed to update company access", type: "error", timeout: 5000 });
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.company-access-manager {
  padding: 1rem;
}

.company-role-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.company-role-table thead th {
  background-color: #f5f5f5;
  font-weight: 600;
}
</style>
